import { Location } from '@angular/common';
import { Component, OnDestroy, OnInit, signal, ViewChild } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ɵFormGroupRawValue,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TiptapEditorComponent } from '@components/tiptap/tiptap-editor/tiptap-editor.component';
import { IPermissions } from '@models/Configuration/IPermissions';
import { Idea } from '@models/Sessions/Idea/Idea';
import { Lang, Language } from '@models/Sessions/Idea/language';
import {
  CheckInconsistenciesResult,
  CheckProblemsResult,
  IssueItem,
  ScriptEvidence,
  SearchMissingFactsResult,
} from '@models/Sessions/Idea/podcast-api';
import { Session } from '@models/Sessions/Session';
import { Paths } from '@models/utils';
import { NgbModal, NgbNav } from '@ng-bootstrap/ng-bootstrap';
import { AssetsPipe } from '@pipes/assets.pipe';
import { BusService } from '@services/bus.service';
import { ConfigService, IInputLengths } from '@services/config.service';
import { EventsService } from '@services/events.service';
import { IIdeaGetResponse } from '@services/sessions/idea.request.service';
import { PodcastService } from '@services/sessions/podcast.service';
import { IdeaSocketService } from '@services/sockets/idea.socket.service';
import {
  cleanupArtifacts,
  convertToHtmlFormat,
  convertToMarkdown,
  convertToMarkdownFormat,
  convertToTextFormat,
  exposeLeadingHeading,
  getHtmlDiff,
  parseProsemirrorContet,
} from '@utils/text-content';
import FileSaver from 'file-saver';
import { defaultsDeep, isObject, mapValues, merge, pick } from 'lodash';
import { ConfirmationService, MessageService } from 'primeng/api';
import { interval, map, Observable, Subject, Subscription, tap } from 'rxjs';

import { environment } from '../../../../../environments/environment';
import { IdeaEvidence } from '../../../../models/Sessions/Idea/evidence';
import { languages } from '../constants';
import { IdeaModalComponent } from '../idea-modal/idea-modal.component';
import { RegenerateIdeaModalComponent } from '../regenerate-idea-modal/regenerate-idea-modal.component';
import { UserFeedbackComponent } from '../user-feedback/user-feedback.component';

const ongoingProcesses = {
  regeneratingScript: 'Regenerating the script',
  improvingScript: 'Improving the script',
  addingUniqueFacts: 'Adding unique facts',
  searchingUniqueFacts: 'Searching unique facts',
  searchingMissingFacts: 'Searching missing facts',
  generatingBulletpoints: 'Turning into bulletpoints',
  addingSelectedFacts: 'Adding selected facts',
  generatingPhoneticScript: 'Generating phonetic script',
  generatingAudioSnippet: 'Generating audio snippet',
  applyingCustomInstructions: 'Applying your instructions',
  expandingMarkedText: 'Expanding marked text',
  translating: 'Translating',
  checkingProblemsAndInconsistencies: 'Checking for issues within the text',
  solvingProblems: 'Solving problems',
} as const;

type OngoingProcess = (typeof ongoingProcesses)[keyof typeof ongoingProcesses];

interface IdeaForm {
  outline: FormControl<string>;
  textScript: FormGroup<
    { original: FormGroup<{ value: FormControl<string> }> } & Partial<
      Record<`lang_${Lang}`, FormGroup<{ value: FormControl<string> }>>
    >
  >;
  phoneticScript: FormGroup<{
    original: FormGroup<{
      value: FormControl<string>;
    }>;
  }>;
}

enum ExportType {
  Text,
  Markdown,
  HTML,
}

@Component({
  selector: 'app-idea-details',
  templateUrl: './idea.component.html',
  styleUrls: ['./idea.component.scss'],
})
export class IdeaDetailsComponent implements OnInit, OnDestroy {
  @ViewChild(NgbNav)
  nav: NgbNav;
  @ViewChild(TiptapEditorComponent)
  textEditor: TiptapEditorComponent;
  @ViewChild(UserFeedbackComponent)
  userFeedback: UserFeedbackComponent;

  session: Session;
  idea: Idea;
  ideaForm: FormGroup<IdeaForm>;

  ongoingProcess = signal<OngoingProcess>(null);
  foundAdditionalFacts: ScriptEvidence[] | null = null;
  audioSnippetUrl: string | null = null;
  explanation: string[] = [];
  loading = true;
  descValid = true;

  preEditJSON: string;

  activeTab = 'tab-0';
  activeUpload = false;
  lockInterval: Subscription;
  recheckInterval: Subscription;
  isEditing = false;

  permissions: IPermissions;
  inputLengths: IInputLengths;

  _titleSuggestions = [];
  _onelinerSuggestions = [];
  titleChange = new Subject<string>();
  onelinerChange = new Subject<string>();
  // changeSub = new Subscription();
  disableEditing = false;
  customInstruction = new FormControl();
  shouldHideCitations = false;
  languages = languages;
  activeScriptTab = 'text-script';
  textScriptLanguages: Language[] = [];
  selectedLanguage: Language;
  missingFactsResult: SearchMissingFactsResult;
  improveScriptLabel = 'Improve script';
  ideaFeatureFlags = {
    regenerateScript: true,
    missingFacts: false,
    checkProblems: false,
    showAllCitations: false,
    showExpandContextMenu: false,
  };
  featureFlags = environment.featureFlags;
  inconsistency: string | null = null;
  inconsistenciesResult = signal<CheckInconsistenciesResult>(null);
  problemsResult: CheckProblemsResult | null = null;
  ExportType = ExportType;

  constructor(
    public appConfig: ConfigService,
    private bus: BusService,
    private events: EventsService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private ideaSocket: IdeaSocketService,
    private podcastService: PodcastService,
    private modalService: NgbModal,
    private formBuilder: FormBuilder,
    private location: Location,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private assetsPipe: AssetsPipe,
  ) {
    this.permissions = appConfig.permissions;
    this.inputLengths = appConfig.inputLengths;
    this.createIdeaForm();
  }

  get isEmpty() {
    if (!this.idea) return false;

    // If last_changed is newer than created_at, it means that the user has saved it at least once
    if (this.idea.last_changed !== this.idea.created_at) {
      return false;
    }

    return (
      // The Project is empty if it has no title, no description, and no one-liner
      !this.idea.textScript.original.value &&
      !this.idea.one_liner &&
      this.idea.title === 'Untitled'
    );
  }

  get sessionThumbnailImage() {
    return this.assetsPipe.transform(
      this.session.config.thumbnail_id || 'image/default_session_thumbnail.png',
    );
  }

  get ideaThumbnailImage() {
    return this.assetsPipe.transform(
      this.idea.thumbnail_id || 'image/default_idea_thumbnail.png',
    );
  }

  get isCheckDisabled() {
    return !this.ideaFeatureFlags.missingFacts || this.disableEditing;
  }

  // Editing Actions

  toggleEditMode(value: boolean) {
    this.isEditing = value;

    if (this.isEditing) {
      this.editIdea();
    } else {
      this.isEditing = !this.dismissChanges();
    }
  }

  editIdea() {
    this.shouldHideCitations = false;
    this.bus.publish(
      this.events.requested.data.session.idea.editable,
      this.idea,
      this.session,
    );
  }

  updateRawDesc(desc: string) {
    this.idea.description = desc;
  }

  getEditedIdea() {
    const idea = merge({}, this.idea, this.ideaForm.getRawValue());
    return idea;
  }

  ideaEditable(response) {
    // this.preEditJSON = JSON.stringify(this.getEditedIdea());
    response.editable = true;
    if (!response.editable) {
      alert('Idea is currently being edited. Please try again later.');
    } else {
      this.isEditing = true;

      if (this.featureFlags.projectEdit.polling) {
        this.lockInterval = interval(5000).subscribe(
          this.setLockStatus.bind(this),
        );

        this.recheckInterval = interval(2000).subscribe(
          this.runRecheck.bind(this),
        );

        this.bus.publish(
          this.events.requested.action.session.idea.lock,
          true,
          this.idea,
          this.session,
        );
      }
    }
  }

  setLockStatus() {
    this.bus.publish(
      this.events.requested.action.session.idea.lock,
      true,
      this.idea,
      this.session,
    );
  }

  runRecheck() {
    this.bus.publish(
      this.events.requested.data.session.idea.single,
      this.idea._id,
      this.session._id,
    );
  }

  recheckIdea(response: IIdeaGetResponse) {
    const scopeJson = JSON.stringify(this.getEditedIdea());
    const responseJson = JSON.stringify(response.idea);
    if (scopeJson !== responseJson) {
      if (!this.isEditing) {
        this.idea = response.idea;
      } else {
        if (this.preEditJSON !== responseJson) {
          this.preEditJSON = responseJson;
          // alert(
          //   'Idea has been modified while you were away. Please copy your progress somewhere else and reload the page.',
          // );
        }
      }
    }
  }

  discardChanges() {
    if (this.preEditJSON) {
      this.idea = JSON.parse(this.preEditJSON);
    }
    this.preEditJSON = undefined;
  }

  get hasUnsavedChanges() {
    return (
      this.preEditJSON &&
      this.preEditJSON !== JSON.stringify(this.getEditedIdea())
    );
  }

  dismissChanges() {
    if (this.hasUnsavedChanges) {
      if (confirm('Are you sure you want to discard the changes?')) {
        this.discardChanges();
        return this.dismissChanges();
      } else {
        return false;
      }
    } else {
      this.isEditing = false;

      if (this.featureFlags.projectEdit.polling) {
        this.lockInterval.remove(this.lockInterval);
        this.lockInterval.unsubscribe();
        this.lockInterval = undefined;
        this.recheckInterval.remove(this.recheckInterval);
        this.recheckInterval.unsubscribe();
        this.recheckInterval = undefined;

        this.bus.publish(
          this.events.requested.action.session.idea.lock,
          false,
          this.idea,
          this.session,
        );
      }

      return true;
    }
  }

  saveIdea(keepEditing = false) {
    this.patchIdeaWithFormValue();

    this.isEditing = keepEditing;

    if (this.descValid) {
      this.bus.publish(
        this.events.requested.action.session.idea.update,
        this.idea,
        this.session,
      );
      this.bus.publish(
        this.events.requested.action.tracking.track,
        this.session,
        'idea',
        'saved',
        this.idea._id,
      );
    }
  }

  updateSuccess() {
    this.preEditJSON = undefined;

    if (this.featureFlags.projectEdit.polling) {
      this.lockInterval.remove(this.lockInterval);
      this.lockInterval.unsubscribe();
      this.lockInterval = undefined;
      this.recheckInterval.remove(this.recheckInterval);
      this.recheckInterval.unsubscribe();
      this.recheckInterval = undefined;

      this.bus.publish(
        this.events.requested.action.session.idea.lock,
        false,
        this.idea,
        this.session,
      );
    }
  }

  get suggestedTags(): string[] {
    return this._titleSuggestions
      .concat(
        this._onelinerSuggestions.filter(
          s => !this._titleSuggestions.includes(s),
        ),
      )
      .filter(tag => !this.idea.tags || !this.idea.tags.includes(tag));
  }

  addTag(tag: string) {
    if (!this.idea.tags) {
      this.idea.tags = [];
    }

    this.idea.tags.push(tag);
  }

  // Idea Actions

  follow() {
    this.bus.publish(
      this.events.requested.action.session.idea.follow,
      this.idea,
      this.session,
    );
  }

  unfollow() {
    this.bus.publish(
      this.events.requested.action.session.idea.unfollow,
      this.idea,
      this.session,
    );
  }

  publish() {
    this.bus.publish(
      this.events.requested.action.session.idea.publish,
      this.idea,
      this.session,
    );
    this.idea.published = true;
  }

  unpublish() {
    this.bus.publish(
      this.events.requested.action.session.idea.unpublish,
      this.idea,
      this.session,
    );
    this.idea.published = false;
  }

  handleDeleteClick() {
    this.confirmationService.confirm({
      message: `Are you sure you want to delete the <strong>${this.idea.title}</strong> Project?`,
      header: 'Confirmation',
      closeOnEscape: true,
      rejectButtonProps: {
        label: 'Cancel',
        severity: 'secondary',
        outlined: true,
      },
      acceptButtonProps: {
        label: 'Delete',
        severity: 'danger',
      },
      accept: () => {
        this.deleteIdea();
        this.router.navigate(['/topics/' + this.session._id], {
          state: { ignoreGuard: true },
        });
      },
    });
  }

  deleteIdea() {
    this.bus.publish(
      this.events.requested.action.session.idea.delete,
      this.idea,
      this.session,
    );
  }

  uploadStart() {
    this.activeUpload = true;
  }

  uploadComplete(key: string) {
    this.activeUpload = false;
    this.idea.thumbnail_id = key;
  }

  ideaReceive(response: IIdeaGetResponse) {
    if (!this.idea && !this.isEditing) {
      this.idea = this.withIdeaDefaults(response.idea);
      this.ideaFeatureFlags.regenerateScript = !!this.idea.inputParams;
      if (
        this.idea.inputParams &&
        'inputUrlOrTextList' in this.idea.inputParams
      ) {
        this.improveScriptLabel = 'Spoken style';
        this.ideaFeatureFlags.missingFacts = true;
        this.ideaFeatureFlags.checkProblems = true;
        this.ideaFeatureFlags.showAllCitations = true;
        this.ideaFeatureFlags.showExpandContextMenu = true;
      }
      this.setIdeaFormValue();
      this.setTextScriptLanguages();
      setTimeout(() => (this.loading = false), 200);
      this.session = response.session;
      this.ideaSocket.syncUpdates(this.session, this.idea);

      this.titleChange.next(this.idea.title);
      this.onelinerChange.next(this.idea.one_liner);

      // Enter edit mode (if asked for it)
      this.activatedRoute.queryParams.subscribe(queryParams => {
        if (queryParams.edit === 'true') {
          this.bus.publish(
            this.events.requested.data.session.idea.editable,
            this.idea,
            this.session,
          );
        }
      });

      setTimeout(() => {
        document
          .querySelector('main')
          .scrollTo({ top: 350, behavior: 'instant' });
      });
    } else {
      this.recheckIdea(response);
    }
  }

  ideaError(error) {
    console.error(error);
    this.router.navigate(['not-found']);
  }

  updateError(error) {
    alert('Could not save the idea!');
    console.error(error);
  }

  subscribe() {
    this.bus.subscribe(
      this.events.received.data.session.idea.single.success,
      this.ideaReceive.bind(this),
    );
    this.bus.subscribe(
      this.events.received.data.session.idea.single.failure,
      this.ideaError.bind(this),
    );
    this.bus.subscribe(
      this.events.received.data.session.idea.editable.success,
      this.ideaEditable.bind(this),
    );
    this.bus.subscribe(
      this.events.received.data.session.idea.editable.failure,
      this.ideaError.bind(this),
    );
    this.bus.subscribe(
      this.events.received.action.session.idea.update.success,
      this.updateSuccess.bind(this),
    );
    this.bus.subscribe(
      this.events.received.action.session.idea.update.failure,
      this.updateError.bind(this),
    );
  }

  unsubscribe() {
    this.bus.unsubscribe(
      this.events.received.data.session.idea.single.success,
      this.ideaReceive.bind(this),
    );
    this.bus.unsubscribe(
      this.events.received.data.session.idea.single.failure,
      this.ideaError.bind(this),
    );
    this.bus.unsubscribe(
      this.events.received.data.session.idea.editable.success,
      this.ideaEditable.bind(this),
    );
    this.bus.unsubscribe(
      this.events.received.data.session.idea.editable.failure,
      this.ideaError.bind(this),
    );
    this.bus.unsubscribe(
      this.events.received.action.session.idea.update.success,
      this.updateSuccess.bind(this),
    );
    this.bus.unsubscribe(
      this.events.received.action.session.idea.update.failure,
      this.updateError.bind(this),
    );
  }

  ngOnInit() {
    this.subscribe();

    const inconsistency = (
      (this.router.getCurrentNavigation()?.extras.state ??
        this.location.getState()) as Record<string, string>
    )?.inconsistency;

    if (inconsistency) {
      this.inconsistency =
        inconsistency === 'N/A'
          ? 'No inconsistencies were identified'
          : inconsistency;
    }

    this.activatedRoute.params.subscribe(params => {
      // this.bus.publish(this.events.requested.data.session.single, params.sessionId);
      this.bus.publish(
        this.events.requested.data.session.idea.single,
        params.ideaId,
        params.sessionId,
      );
    });

    // this.changeSub.add(
    //   this.titleChange
    //     .pipe(
    //       debounceTime(300),
    //       filter(
    //         () => !this.session || this.session.config.suggest_tags !== false,
    //       ),
    //       switchMap(text => this.entRecog.getEntities(text)),
    //     )
    //     .subscribe(res => (this._titleSuggestions = res as any)),
    // );

    // this.changeSub.add(
    //   this.onelinerChange
    //     .pipe(
    //       debounceTime(300),
    //       filter(
    //         () => !this.session || this.session.config.suggest_tags !== false,
    //       ),
    //       switchMap(text => this.entRecog.getEntities(text)),
    //     )
    //     .subscribe(res => (this._onelinerSuggestions = res as any)),
    // );
  }

  ngOnDestroy() {
    this.unsubscribe();
    this.ideaSocket.unsyncUpdates();
    if (this.recheckInterval) {
      this.recheckInterval.remove(this.recheckInterval);
      this.recheckInterval.unsubscribe();
      this.recheckInterval = undefined;
    }

    this.titleChange.complete();
    this.onelinerChange.complete();
    // this.changeSub.unsubscribe();
  }

  openRegenerateScriptModal() {
    if (!this.idea.inputParams) {
      return;
    }

    const modalRef = this.modalService.open(RegenerateIdeaModalComponent, {
      size: 'lg',
      windowClass: 'big-modal',
    });
    modalRef.componentInstance.inputParams = this.idea.inputParams;

    modalRef.result
      .then((inputParams: Idea['inputParams']) => {
        this.idea.inputParams = inputParams;
        this.regenerateScript();
      })
      .catch(() => void 0);
  }

  regenerateScript() {
    if (!this.idea.inputParams) {
      return;
    }

    if (this.userFeedback) {
      this.userFeedback.clear();
    }

    this.startProcess(ongoingProcesses.regeneratingScript);
    const initialScript = this.cleanupScript(
      this.ideaForm.get('textScript.original.value')!.value,
    );

    const getResponse = (): Observable<{ script: string }> | null => {
      if ('subject' in this.idea.inputParams) {
        return this.podcastService
          .ultimateGenerate({
            document_type: 'podcast',
            topic: this.idea.inputParams.subject,
            context: this.idea.inputParams.context ?? '',
            number_of_sections:
              'sections' in this.idea.inputParams
                ? this.idea.inputParams.sections
                : void 0,
            number_of_guests:
              'guests' in this.idea.inputParams
                ? this.idea.inputParams.guests
                : void 0,
            podcast_name: this.idea.categories[0],
            prompt_language: this.idea.inputParams.promptLanguage,
          })
          .pipe(
            map(data => {
              return {
                script: data.main_text,
                shortTitle: data.short_title,
                oneLiner: data.one_liner,
                evidence: data.evidence_info,
              };
            }),
          );
      }

      if ('userTextType' in this.idea.inputParams) {
        return this.podcastService
          .ultimateGenerate({
            document_type: 'article',
            sources: this.idea.inputParams.inputUrlOrTextList,
            context: this.idea.inputParams.context,
            prompt_language: this.idea.inputParams.promptLanguage,
            article_type: this.idea.inputParams.userTextType,
          })
          .pipe(
            map(data => {
              return {
                script: data.main_text,
                shortTitle: data.short_title,
                oneLiner: data.one_liner,
                evidence: data.evidence_info,
              };
            }),
          );
      }

      if ('styleExamples' in this.idea.inputParams) {
        return this.podcastService
          .ultimateGenerate({
            document_type: 'custom',
            sources: this.idea.inputParams.inputUrlOrTextList,
            context: this.idea.inputParams.context,
            prompt_language: this.idea.inputParams.promptLanguage,
            user_text: this.idea.inputParams.styleExamples[0],
          })
          .pipe(
            map(data => ({
              script: data.main_text,
              shortTitle: data.short_title,
              oneLiner: data.one_liner,
              evidence: data.evidence_info,
            })),
          );
      }

      return null;
    };

    const observable = getResponse();

    if (!observable) {
      throw Error('Inconsistent IdeaInputParams state');
    }

    observable.subscribe(res => {
      this.ideaForm.patchValue({
        textScript: {
          original: {
            value: exposeLeadingHeading(
              initialScript,
              cleanupArtifacts(res.script),
            ),
          },
        },
      });
      if ('evidence' in res) {
        this.idea.evidence = res.evidence as ScriptEvidence[];
      }
      this.endProcess();
      this.activeScriptTab = 'text-script';
    });
  }

  improveScript() {
    this.startProcess(ongoingProcesses.improvingScript);
    const initialScript = this.cleanupScript(
      this.ideaForm.get('textScript.original.value')!.value,
    );
    this.podcastService
      .improveScript(initialScript, null, this.idea.inputParams?.promptLanguage)
      .subscribe(({ script, explanation }) => {
        this.ideaForm.patchValue({
          textScript: {
            original: {
              value: this.getNewScriptDiff(initialScript, script),
            },
          },
        });
        this.explanation = [explanation];
        this.endProcess();
      });
  }

  addUniqueFacts() {
    this.startProcess(ongoingProcesses.addingUniqueFacts);
    const initialScript = this.cleanupScript(
      this.ideaForm.get('textScript.original.value')!.value,
    );
    this.podcastService
      .addUniqueFacts(
        initialScript,
        this.idea.evidence,
        this.idea.inputParams?.promptLanguage,
      )
      .subscribe(({ script, evidence }) => {
        this.ideaForm.patchValue({
          textScript: {
            original: {
              value: this.getNewScriptDiff(initialScript, script),
            },
          },
        });
        this.idea.evidence = [...this.idea.evidence, ...evidence];
        this.endProcess();
      });
  }

  displayUniqueFacts() {
    this.startProcess(ongoingProcesses.searchingUniqueFacts);
    this.podcastService
      .searchUniqueFacts(
        this.cleanupScript(
          this.ideaForm.get('textScript.original.value')!.value,
        ),
        this.idea.evidence,
        this.idea.inputParams?.promptLanguage,
      )
      .subscribe(({ evidence }) => {
        this.foundAdditionalFacts = evidence;
        this.endProcess();
      });
  }

  findMissingFacts() {
    if (
      !this.ideaFeatureFlags.missingFacts ||
      !(this.idea.inputParams && 'inputUrlOrTextList' in this.idea.inputParams)
    ) {
      return;
    }

    this.startProcess(ongoingProcesses.searchingMissingFacts);
    this.podcastService
      .searchMissingFacts(
        this.cleanupScript(
          this.ideaForm.get('textScript.original.value')!.value,
        ),
        {
          ...this.idea.inputParams,
          inputUrlOrTextList: [
            ...this.idea.inputParams.inputUrlOrTextList,
            // Take extra evidence from addUniqueFacts. These evidence have both url and content (Summary)
            ...this.idea.evidence
              .filter(evidence => evidence.content && evidence.url)
              .map(evidence => evidence.url),
          ],
        },
      )
      .subscribe(missingFactsResult => {
        this.missingFactsResult = missingFactsResult;
        this.endProcess();
      });
  }

  generateBulletpoints() {
    this.startProcess(ongoingProcesses.generatingBulletpoints);
    const initialScript = this.cleanupScript(
      this.ideaForm.get('textScript.original.value')!.value,
    );
    this.podcastService
      .generateBulletpoints(
        initialScript,
        this.idea.inputParams?.promptLanguage,
      )
      .subscribe(({ script, explanation }) => {
        this.ideaForm.patchValue({
          textScript: {
            original: {
              value: exposeLeadingHeading(
                initialScript,
                cleanupArtifacts(script),
              ),
            },
          },
        });
        this.explanation = [explanation];
        this.endProcess();
      });
  }

  submitCustomInstruction() {
    if (!this.customInstruction.value) {
      return;
    }

    this.startProcess(ongoingProcesses.applyingCustomInstructions);
    const initialScript = this.cleanupScript(
      this.ideaForm.get('textScript.original.value')!.value,
    );
    (this.idea.inputParams && 'inputUrlOrTextList' in this.idea.inputParams
      ? this.podcastService.improveTextBasedOnUserCustomInstructionAndExtraContent(
          initialScript,
          this.customInstruction.value,
          this.idea.inputParams,
        )
      : this.podcastService.improveScriptWithCustomInstruction(
          initialScript,
          this.customInstruction.value,
          this.idea.inputParams?.promptLanguage,
        )
    ).subscribe(({ script, explanation }) => {
      this.ideaForm.patchValue({
        textScript: {
          original: {
            value: script,
          },
        },
      });
      this.explanation = [explanation];
      this.endProcess();
    });
  }

  integrateFacts(evidence?: ScriptEvidence[]) {
    this.foundAdditionalFacts = null;

    if (!evidence?.length) {
      return;
    }

    this.startProcess(ongoingProcesses.addingSelectedFacts);
    const initialScript = this.cleanupScript(
      this.ideaForm.get('textScript.original.value')!.value,
    );
    this.podcastService
      .integrateFacts(
        initialScript,
        this.idea.evidence,
        evidence,
        this.idea.inputParams?.promptLanguage,
      )
      .subscribe(({ script, reasonings }) => {
        this.ideaForm.patchValue({
          textScript: {
            original: {
              value: this.getNewScriptDiff(initialScript, script),
            },
          },
        });
        this.idea.evidence = [...this.idea.evidence, ...evidence];
        this.explanation = reasonings;
        this.endProcess();
      });
  }

  integrateMissingFacts(facts?: IssueItem[]) {
    const missingFactsResult = this.missingFactsResult;
    this.missingFactsResult = null;

    if (!missingFactsResult || !facts?.length) {
      return;
    }

    this.startProcess(ongoingProcesses.addingSelectedFacts);
    const initialScript = this.cleanupScript(
      this.ideaForm.get('textScript.original.value')!.value,
    );
    this.podcastService
      .addMissingFactsWithReasoning(
        initialScript,
        missingFactsResult.missing_facts_description,
        facts,
        this.idea.inputParams.promptLanguage,
      )
      .subscribe(({ article, explanation }) => {
        this.ideaForm.patchValue({
          textScript: {
            original: {
              value: this.getNewScriptDiff(initialScript, article),
            },
          },
        });
        this.explanation = [explanation];
        this.endProcess();
      });
  }

  solveProblems(problems?: IssueItem[]) {
    const problemsResult = this.problemsResult;
    this.problemsResult = null;

    if (!problemsResult || !problems?.length) {
      return;
    }

    this.startProcess(ongoingProcesses.solvingProblems);
    const initialScript = this.cleanupScript(
      this.ideaForm.get('textScript.original.value')!.value,
    );
    this.podcastService
      .solveProblemsWithReasoning(
        initialScript,
        problemsResult.problems_description,
        problems,
        this.idea.inputParams.promptLanguage,
      )
      .subscribe(({ article, explanation }) => {
        this.ideaForm.patchValue({
          textScript: {
            original: {
              value: this.getNewScriptDiff(initialScript, article),
            },
          },
        });
        this.explanation = [explanation];
        this.endProcess();
      });
  }

  expandText(userInstruction?: string) {
    const markedText = this.textEditor.getSelection();

    if (!markedText) {
      return;
    }

    this.startProcess(
      userInstruction
        ? ongoingProcesses.applyingCustomInstructions
        : ongoingProcesses.expandingMarkedText,
    );
    const initialScript = this.cleanupScript(
      this.ideaForm.get('textScript.original.value')!.value,
    );
    this.podcastService
      .expandText(
        markedText,
        initialScript,
        this.idea.evidence,
        this.idea.inputParams.promptLanguage,
        userInstruction,
      )
      .subscribe(({ article }) => {
        if (article) {
          this.ideaForm.patchValue({
            textScript: {
              original: {
                value: this.getNewScriptDiff(initialScript, article),
              },
            },
          });
        } else {
          this.explanation = [
            'Could not find additional information in the sources suitable to expand the selected text',
          ];
        }
        this.endProcess();
      });
  }

  generatePhoneticScript() {
    this.startProcess(ongoingProcesses.generatingPhoneticScript);
    this.podcastService
      .phoneticSpellingPolish(
        this.cleanupScript(
          this.ideaForm.get('textScript.original.value')!.value,
        ),
        this.idea.inputParams?.promptLanguage,
      )
      .pipe(
        tap(({ script }) => {
          const currentValue = this.ideaForm.get(
            'phoneticScript.original.value',
          )!.value;
          const cleanCurrentValue = this.cleanupScript(currentValue);

          this.ideaForm.patchValue({
            phoneticScript: {
              original: {
                value: this.isTipTapContentEmpty(cleanCurrentValue)
                  ? script
                  : this.getNewScriptDiff(cleanCurrentValue, script),
              },
            },
          });
          this.saveIdea(true);
          this.endProcess();
        }),
      )
      .subscribe();
  }

  generatingAudioSnippet() {
    const selectedText = convertToMarkdown(
      this.idea.phoneticScript.original.value,
    );

    if (!selectedText) {
      return;
    }

    this.startProcess(ongoingProcesses.generatingAudioSnippet);
    this.podcastService.textToSpeech(selectedText).subscribe(audioUrl => {
      this.audioSnippetUrl = audioUrl;
      this.endProcess();
    });
  }

  downloadScript() {
    const script = this.getTextToExport(
      ExportType.Text,
      this.idea.evidence,
      this.shouldHideCitations,
    );

    if (script) {
      FileSaver.saveAs(
        new Blob([script], { type: 'text/plain;charset=utf-8' }),
        `${this.idea.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.txt`,
      );
    }
  }

  copyScriptToClipboard(type: ExportType) {
    const script = this.getTextToExport(type);

    if (script) {
      navigator.clipboard.writeText(script);
    }
  }

  check() {
    const params = this.idea.inputParams;

    if (
      params &&
      'inputUrlOrTextList' in params &&
      params.inputUrlOrTextList.some(Boolean)
    ) {
      return this.checkProblemsAndInconsistencies();
    }

    return this.checkInconsistencies();
  }

  checkProblemsAndInconsistencies() {
    if (!('inputUrlOrTextList' in this.idea.inputParams)) return;

    this.startProcess(ongoingProcesses.checkingProblemsAndInconsistencies);
    this.podcastService
      .checkProblemsAndInconsistencies(
        [
          ...this.idea.inputParams.inputUrlOrTextList,
          // Take extra evidence from addUniqueFacts. These evidence have both url and content (Summary)
          ...this.idea.evidence
            .filter(evidence => evidence.content && evidence.url)
            .map(evidence => evidence.url),
        ],
        this.cleanupScript(
          this.ideaForm.get('textScript.original.value')!.value,
        ),
        this.idea.inputParams.promptLanguage,
      )
      .subscribe({
        next: problemsAndInconsistenciesResult => {
          const {
            inconsistencies_description,
            inconsistencies_list,
            problems_description,
            problems_list,
          } = problemsAndInconsistenciesResult;

          this.inconsistenciesResult.set({
            inconsistencies_description,
            inconsistencies_list,
          });
          this.problemsResult = { problems_description, problems_list };
          this.endProcess();
        },
        error: () => {
          this.endProcess();
          this.messageService.add({
            severity: 'error',
            summary: 'Something went wrong.',
            detail: 'Please try change input data or try later.',
            sticky: true,
          });
        },
      });
  }

  checkInconsistencies() {
    this.startProcess(ongoingProcesses.checkingProblemsAndInconsistencies);
    this.podcastService
      .checkInconsistencies(
        this.cleanupScript(
          this.ideaForm.get('textScript.original.value')!.value,
        ),
        this.idea.inputParams.promptLanguage,
      )
      .subscribe({
        next: inconsistenciesResult => {
          this.endProcess();
          this.inconsistenciesResult.set(inconsistenciesResult);
        },
        error: () => {
          this.endProcess();
          this.messageService.add({
            severity: 'error',
            summary: 'Something went wrong.',
            detail: 'Please try change input data or try later.',
            sticky: true,
          });
        },
      });
  }

  private getTextToExport(
    type: ExportType = ExportType.Text,
    evidence: IdeaEvidence[] = [],
    hideCitations = true,
  ) {
    const ideaFormValue = this.ideaForm.getRawValue();
    const convertFn =
      type === ExportType.Text
        ? convertToTextFormat
        : type === ExportType.Markdown
          ? convertToMarkdownFormat
          : convertToHtmlFormat;
    let rawText = '';

    switch (this.activeScriptTab) {
      case 'text-script': {
        rawText = convertFn(
          this.isEditing
            ? ideaFormValue.textScript.original.value
            : this.idea.textScript.original.value,
          evidence,
          hideCitations,
        );
        break;
      }
      case 'phonetic-script': {
        rawText = convertFn(
          this.isEditing
            ? ideaFormValue.phoneticScript.original.value
            : this.idea.phoneticScript.original.value,
          [],
          hideCitations,
        );
        break;
      }
      case 'translations': {
        rawText = this.selectedLanguage
          ? convertFn(
              this.isEditing
                ? ideaFormValue.textScript[`lang_${this.selectedLanguage.code}`]
                    .value
                : this.idea.textScript[`lang_${this.selectedLanguage.code}`]
                    .value,
              [],
              hideCitations,
            )
          : null;
        break;
      }
      default:
        rawText = '';
        break;
    }

    if (type === ExportType.Text) {
      return `${this.idea.title}\n${this.idea.one_liner}\n${rawText}`;
    } else if (type === ExportType.Markdown) {
      return `# ${this.idea.title}\n## ${this.idea.one_liner}\n\n${rawText}`;
    } else {
      return `<h1>${this.idea.title}</h1><h2>${this.idea.one_liner}</h2>${rawText}`;
    }
  }

  translate(lang: Language) {
    this.startProcess(ongoingProcesses.translating);
    const initialScript = this.cleanupScript(
      this.ideaForm.get('textScript.original.value')!.value,
    );
    this.podcastService
      .translate(initialScript, lang.code)
      .subscribe(script => {
        const currentValue =
          this.ideaForm.get(`textScript.lang_${lang.code}.value`)?.value || '';
        const cleanCurrentValue = this.cleanupScript(currentValue);

        this.ideaForm.patchValue({
          textScript: {
            [`lang_${lang.code}`]: {
              value: this.isTipTapContentEmpty(cleanCurrentValue)
                ? script
                : this.getNewScriptDiff(cleanCurrentValue, script),
            },
          },
        });
        this.saveIdea(true);
        this.endProcess();
        this.setTextScriptLanguages();
        this.selectLanguage(lang);
      });
  }

  selectLanguage(lang: Language) {
    this.activeScriptTab = 'translations';
    this.selectedLanguage = lang;
  }

  openIdeaModal() {
    const modalRef = this.modalService.open(IdeaModalComponent, {
      size: 'lg',
      windowClass: 'big-modal',
    });
    (modalRef.componentInstance as IdeaModalComponent).bindData({
      session: this.session,
      idea: this.idea,
      compactView: true,
    });
  }

  acceptChanges(path: Paths<ɵFormGroupRawValue<IdeaForm>>) {
    const formControl = this.ideaForm.get(path) as AbstractControl<{
      value: string;
    }>;
    formControl.patchValue({
      value: this.cleanupScript(formControl.value.value),
    });
    this.explanation = [];
  }

  private setTextScriptLanguages() {
    this.textScriptLanguages = Object.keys(this.idea.textScript)
      .filter(lang => !!this.idea.textScript[lang]?.value)
      .map(lang => {
        const code = lang.replace('lang_', '');
        return this.languages.find(l => l.code === code);
      })
      .filter(Boolean);
  }

  clearProcessResult() {
    this.foundAdditionalFacts = null;
    this.audioSnippetUrl = null;
    this.missingFactsResult = null;
    this.explanation = [];
  }

  private startProcess(process: OngoingProcess) {
    this.clearProcessResult();
    this.disableEditing = true;
    this.ideaForm.disable();
    this.ongoingProcess.set(process);
  }

  private endProcess() {
    this.ongoingProcess.set(null);
    this.disableEditing = false;
    this.ideaForm.enable();
    this.textEditor?.unsetHighlight();
  }

  private getNewScriptDiff(initialScript: string, newScript: string) {
    return getHtmlDiff(
      initialScript,
      exposeLeadingHeading(initialScript, cleanupArtifacts(newScript)),
    );
  }

  private patchIdeaWithFormValue() {
    merge(this.idea, this.getCleanFormValue(this.ideaForm.getRawValue()));
  }

  private getCleanFormValue(formValue: unknown) {
    return mapValues(formValue as object, v => {
      if (isObject(v)) {
        return this.getCleanFormValue(v);
      }
      return this.cleanupScript(v);
    });
  }
  /**
   * Cleans up a script string by removing HTML deletion markers and content, and insertion markers while keeping their content.
   *
   * This method:
   * 1. Removes <del> tags and their content, handling adjacent spaces to prevent double spaces
   * 2. Removes <ins> and </ins> tags while preserving their content
   * 3. Normalizes spaces to ensure clean formatting
   *
   * The updated regex patterns ensure proper spacing when content is removed.
   *
   * @example
   * // Removing deleted text without creating extra spaces
   * cleanupScript("This is <del>deleted</del> text") // Returns: "This is text"
   *
   * @example
   * // Removing insertion tags but keeping content
   * cleanupScript("This is <ins>inserted</ins> text") // Returns: "This is inserted text"
   *
   * @example
   * // Handling both cases
   * cleanupScript("This <del>was</del><ins>is</ins> text") // Returns: "This is text"
   *
   * @param script - The string to clean up
   * @returns The cleaned string with deletion content removed and insertion tags stripped
   */
  private cleanupScript(script: string) {
    return script
      .replace(/\s?(<del>)+[^<]*(<\/del>)+\s?/gm, ' ') // Remove deleted content with surrounding spaces
      .replace(/<\/?ins>/gm, '') // Remove insertion tags
      .replace(/\s{2,}/g, ' ') // Normalize consecutive spaces
      .trim(); // Remove any leading/trailing spaces
  }

  private withIdeaDefaults(idea: Idea) {
    return defaultsDeep(idea, {
      outline: '',
      textScript: {
        original: {
          value: parseProsemirrorContet(idea.descriptionRich) || '',
          v: 0,
        },
      },
      evidence: [],
      phoneticScript: {
        original: {
          value: '',
          v: 0,
        },
      },
    });
  }

  private createIdeaForm() {
    this.ideaForm = this.formBuilder.group<IdeaForm>({
      outline: this.formBuilder.control<string>('', {
        nonNullable: true,
      }),
      textScript: this.formBuilder.group({
        original: this.getScriptValueFormGroup(),
        ...languages.reduce(
          (agg, lang) => ({
            ...agg,
            [`lang_${lang.code}`]: this.getScriptValueFormGroup(),
          }),
          {},
        ),
      }),
      phoneticScript: this.formBuilder.group({
        original: this.getScriptValueFormGroup(),
      }),
    });
  }

  private getScriptValueFormGroup() {
    return this.formBuilder.group({
      value: this.formBuilder.control<string>('', {
        nonNullable: true,
      }),
    });
  }

  private setIdeaFormValue() {
    this.ideaForm.patchValue(
      pick(this.idea, ['outline', 'textScript', 'phoneticScript']),
    );
  }

  /**
   * Checks if TipTap content is empty or contains only empty elements
   * @param content - The HTML content from TipTap editor
   * @returns true if content is empty or contains only empty elements
   */
  private isTipTapContentEmpty(content: string): boolean {
    if (!content || content.trim() === '') {
      return true;
    }

    // Remove all HTML tags and check if there's any meaningful text content
    const textContent = content
      .replace(/<[^>]*>/g, '') // Remove all HTML tags
      .replace(/&nbsp;/g, ' ') // Replace non-breaking spaces
      .replace(/\s+/g, ' ') // Normalize whitespace
      .trim();

    return textContent === '';
  }

  goTo(id: string) {
    this.nav.select(id);
  }
}
